// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import dayjs, { Dayjs } from "dayjs";
import { COLOURS  } from '../constants/constants';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface ChartProps {
  data: any;
  distinctProductNames?: string[];
  distinctPriorities?: string[];
  from: Dayjs;
  to: Dayjs;
  chartType: "product" | "priority" | "state";
}

const CSBarChart: React.FC<ChartProps> = ({
  data,
  distinctProductNames = [],
  distinctPriorities = [],
  from,
  to,
  chartType,
}) => {
  const chartRef = useRef(null);
  const monthLabels : string[] = [];
  let currentMonth = from.startOf('month');

  while (currentMonth.isBefore(to) || currentMonth.isSame(to, "month")) {
    monthLabels.push(currentMonth.format("MMM")); 
    currentMonth = currentMonth.add(1, "month");
  }

  let chartData: { labels: string[]; datasets: any[] } = { labels: [], datasets: [] };
  let maxCount = 0;
  const greyColor = "#25AAE1";

  if (chartType === "product") {
    const productColors: { [key: string]: string } = {
      "WSO2 API Manager": "#25AAE1",
      "WSO2 API Manager Analytics": "#3EB5E5",
      "WSO2 API Platform for Kubernetes": "#57C0E9",
      "Ballerina": "##2299CC",
      "WSO2 Enterprise Integrator": "#1F84A6",
      "WSO2 Identity Server": "#1A6D8B",
      "WSO2 Micro Integrator": "#70CBED",
      "Unknown": greyColor
    };
    const datasets = distinctProductNames.map((productName, index) => ({
      label: productName,
      // backgroundColor: primaryColor,
      backgroundColor: productColors[productName] || COLOURS[index % COLOURS.length],
      data: monthLabels.map((monthLabel, index) => {
        const monthKey = from.add(index, "month").month();
        return data[monthKey]?.[productName] || 0;
    }),
    }));  
    chartData = {
      labels: monthLabels,
      datasets,
    };
    maxCount = Math.max(...datasets.flatMap((dataset) => dataset.data)) || 0;
  } else if (chartType === "priority") {
    const datasets = distinctPriorities.map((priority, index) => ({
      label: priority,
      backgroundColor: priority === "Unknown" ? greyColor : COLOURS[index % COLOURS.length],
      data: monthLabels.map((monthLabel, index) => {
        const monthKey = from.add(index, "month").month();
        return data[monthKey]?.[priority] || 0;
    }),
  }));
    chartData = {
      labels: monthLabels,
      datasets,
    };
    maxCount = Math.max(...datasets.flatMap((dataset) => dataset.data)) || 0;
  } else if (chartType === "state") {
    chartData = {
      labels: data.map((entry: any) => entry.day),
      datasets: [
        {
          label: "Opened",
          data: data.map((entry: any) => entry.opened),
          backgroundColor: "#25AAE1", 
          borderWidth: 1,
        },
        {
          label: "Resolved",
          data: data.map((entry: any) => entry.closed),
          backgroundColor: data.some((entry: any) => entry.state === "Unknown")
            ? greyColor
            : "#1A6D8B", 
          borderWidth: 1,
        },
      ],
    };
    maxCount = Math.max(...data.flatMap((entry: any) => [entry.opened, entry.closed]));
  }
//   }  else if (chartType === "volume") {
//     chartData = {
//         labels: data.map((entry: any) => entry.yearAndMonth),
//         datasets: [
//             {
//                 label: "Incident Cases",
//                 data: data.map((entry: any) => entry.incidentCount),
//                 backgroundColor: "#25AAE1", 
//             },
//             {
//                 label: "Query Cases",
//                 data: data.map((entry: any) => entry.queryCount),
//                 backgroundColor: "#1F84A6", 
//             },
//         ],
//     };
//     maxCount = Math.max(...data.flatMap((entry: any) => [entry.incidentCount, entry.queryCount]));
// }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text:
          chartType === "product"
            ? "Cases Created by Product"
            : chartType === "priority"
            ? "Cases Created by Priority"
            : chartType === "state"
            ? "Created vs Resolved Cases"
            : "",
        font: {
          size: 16,
        },
        align: "start" as const,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: chartType === "state" ? "Days" : "Months",
        },
      },
      y: {
        title: {
          display: true,
          text: "Number of Cases",
        },
        ticks: {
          stepSize: 1,
          callback: function (tickValue: string | number) {
            return Number.isInteger(tickValue) ? tickValue : null;
          },
        },
        suggestedMax: Math.ceil(maxCount + 1),
      },
    },
  };

  const hasData = chartData.datasets.some(
    (dataset) => dataset.data.some((value: number) => value > 0)
  );

  return (
    <div style={{ width: "100%", height: "400px" }}>
      {hasData ? (
        <Bar ref={chartRef} data={chartData} options={chartOptions} />
      ) : (
        <p style={{ textAlign: "center" }}>No data available for the selected criteria.</p>
      )}
    </div>
  );
};

export default CSBarChart;
